import React from 'react';
import {
  Box,
  Container,
  Divider,
  Link,
  Paper,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';

const LAST_UPDATED = '19 June 2026';
const CONTACT_EMAIL = 'info@insitech-international.com';

const SECTIONS = [
  {
    title: 'Information We Collect',
    body: `We may collect personal information that you provide to us directly, such as your name, email address, and any other details you choose to share, including your first name, last name, email address, learning progress, lesson notes, and follow-up topics.

For platform security, support, and service improvement, we record limited account activity such as registration, sign-in, page visits, lessons opened, task completion, lesson-note saves, searches, and use of the SOLVE and 5-HOW tools. Audit records contain your account ID, event name, limited context, and a server timestamp. We do not place lesson-note text, passwords, session tokens, browser fingerprints, or full IP addresses in these audit records.`,
  },
  {
    title: 'How We Use Your Information',
    body: 'We use the information we collect to:',
    bullets: [
      'Provide, maintain, and improve our services.',
      'Communicate with you about your account or transactions.',
      "Keep each learner's content and progress separate.",
      'Investigate errors, misuse, and account-support requests.',
      'Analyse aggregate usage and improve the learning experience.',
    ],
  },
  {
    title: 'Data Security',
    body: 'We implement reasonable security measures to protect your information from unauthorised access, use, or disclosure. However, no method of transmission over the internet or method of electronic storage is 100% secure.',
  },
  {
    title: 'Cookies',
    body: 'We use one strictly necessary, HTTP-only authentication cookie to keep you signed in and associate requests with your account. The platform does not currently use advertising, profiling, or third-party analytics cookies. Blocking the authentication cookie will prevent account and progress features from working.',
  },
  {
    title: 'Retention and Access',
    body: 'Account data and learning progress are retained while your account is active. Platform audit events are retained for up to 12 months unless a longer period is required to investigate security, fraud, or legal issues. Access is limited to authorised platform administrators and service providers needed to operate the platform.',
  },
  {
    title: 'Your Choices',
    body: 'You may request access to, correction of, or deletion of your account and associated learning data by contacting us. Deleting an account also deletes its associated progress and audit records, subject to any legal retention requirement.',
  },
  {
    title: 'Changes to This Privacy Policy',
    body: 'We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the effective date at the top of this policy.',
  },
  { title: 'Contact Us', contact: true },
];

const PrivacyPolicy = () => {
  const theme = useTheme();
  const accentColor = theme.palette.primary.main;

  return (
    <Box sx={{ bgcolor: 'background.paper', py: { xs: 6, sm: 8, md: 12 } }}>
      <Container maxWidth="md">

        {/* Header */}
        <Box textAlign="center" mb={{ xs: 5, sm: 7 }}>
          <Box sx={{ display: 'inline-flex', p: 1.5, borderRadius: 2, bgcolor: alpha(accentColor, 0.1), color: accentColor, mb: 2 }}>
            <LockIcon />
          </Box>
          <Typography
            variant="h2"
            fontWeight={800}
            mb={1.5}
            sx={{
              fontSize: { xs: '2rem', sm: '2.75rem', md: '3rem' },
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Privacy Policy
          </Typography>
          <Typography variant="body2" color="text.disabled" mb={2}>
            Effective date: {LAST_UPDATED}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto' }}>
            At InsiTech International, we are committed to protecting your privacy. This Privacy
            Policy outlines how we collect, use, and safeguard your information when you use our
            services, including the Qode Clarity platform.
          </Typography>
        </Box>

        {/* Sections */}
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 3,
            borderColor: alpha(accentColor, 0.2),
            bgcolor: alpha(accentColor, 0.02),
            overflow: 'hidden',
          }}
        >
          {SECTIONS.map((section, idx) => (
            <Box key={section.title}>
              {idx > 0 && <Divider sx={{ borderColor: alpha(accentColor, 0.08) }} />}
              <Box sx={{ px: { xs: 2.5, sm: 4 }, py: { xs: 2.5, sm: 3 } }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  {section.title}
                </Typography>

                {section.body && (
                  <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-line', lineHeight: 1.75 }}>
                    {section.body}
                  </Typography>
                )}

                {section.bullets && (
                  <Box component="ul" sx={{ pl: 2.5, mt: section.body ? 1 : 0, mb: 0, color: 'text.secondary' }}>
                    {section.bullets.map((point) => (
                      <Typography key={point} component="li" variant="body1" sx={{ mb: 0.75, lineHeight: 1.7 }}>
                        {point}
                      </Typography>
                    ))}
                  </Box>
                )}

                {section.contact && (
                  <Typography variant="body1" color="text.secondary">
                    If you have any questions about this Privacy Policy, please contact us at{' '}
                    <Link href={`mailto:${CONTACT_EMAIL}`} color="primary.main" fontWeight={600}>
                      {CONTACT_EMAIL}
                    </Link>
                    .
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Paper>

      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
